import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, CheckCircle2, BookOpen, Award, Globe2, Laptop, Send, Wallet, ClipboardCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHero, Reveal, SectionHeading, Eyebrow } from "../components/common";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";
import { getUtm } from "../lib/utm";
import { OSSD_INTRO, OSSD_WHY, OSSD_CREDITS, OSSD_GRADES, OSSD_PROCESS } from "../mock";

const HIGHLIGHTS = [
  { icon: Globe2, title: "Global university access", desc: "Enter any English-speaking university in the world with a recognised Canadian diploma." },
  { icon: Award, title: "Often no IELTS/TOEFL", desc: "Graduates are unlikely to need English proficiency tests for most international universities." },
  { icon: Laptop, title: "100% online", desc: "Follow the full Canadian Grades 9–12 curriculum from anywhere, at your own pace." },
  { icon: BookOpen, title: "30 credits", desc: "18 compulsory and 12 optional credits, including an assessment of prior learning." },
];

const DIAL_CODES = ["+91", "+60", "+880", "+1", "+61", "+44", "+65"];

function OSSDForm() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", dial_code: "+91", phone: "", grade: "", message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Please fill required fields", description: "Name, email and phone are required.", variant: "destructive" });
      return;
    }
    setSaving(true);
    api.post("/leads", {
      name: form.name, email: form.email, dial_code: form.dial_code, phone: form.phone,
      course: form.grade ? `OSSD Program (${form.grade})` : "OSSD Program",
      message: form.message, source: "website", ...getUtm(),
    }).then(() => {
      setSubmitted(true);
      toast({ title: "Application received! 🎓", description: "Our OSSD team will contact you within 24 hours." });
    }).catch(() => {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    }).finally(() => setSaving(false));
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-slate-100 shadow-xl p-10 text-center" data-testid="ossd-form-success">
        <div className="w-16 h-16 rounded-full bg-[#004030] grid place-items-center text-[#e0c05a] mx-auto"><CheckCircle2 size={32} /></div>
        <h3 className="font-display text-2xl font-bold text-[#012a1e] mt-5">Thank you!</h3>
        <p className="text-slate-500 mt-2">Your OSSD enquiry has been received. Our team will reach out shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 shadow-xl p-7 md:p-9 space-y-5" data-testid="ossd-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm font-medium text-slate-600">Full name *</label><Input data-testid="ossd-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="h-11 mt-1.5" placeholder="Your name" /></div>
        <div><label className="text-sm font-medium text-slate-600">Email *</label><Input data-testid="ossd-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-11 mt-1.5" placeholder="you@email.com" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium text-slate-600">Phone *</label>
          <div className="flex gap-2 mt-1.5">
            <Select value={form.dial_code} onValueChange={(v) => set("dial_code", v)}>
              <SelectTrigger className="h-11 w-24" data-testid="ossd-dial"><SelectValue /></SelectTrigger>
              <SelectContent>{DIAL_CODES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="ossd-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 flex-1" placeholder="Phone number" />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-slate-600">Current grade</label>
          <Select value={form.grade} onValueChange={(v) => set("grade", v)}>
            <SelectTrigger className="h-11 mt-1.5" data-testid="ossd-grade"><SelectValue placeholder="Select grade" /></SelectTrigger>
            <SelectContent>{["Grade 9", "Grade 10", "Grade 11", "Grade 12"].map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><label className="text-sm font-medium text-slate-600">Message</label><Textarea data-testid="ossd-message" value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5" rows={3} placeholder="Tell us about your goals (optional)" /></div>
      <Button type="submit" disabled={saving} data-testid="ossd-submit" className="w-full bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-12 font-semibold">
        {saving ? "Submitting..." : <>Submit OSSD Application <Send size={17} className="ml-1" /></>}
      </Button>
    </form>
  );
}

export default function OSSD() {
  return (
    <>
      <PageHero
        eyebrow="Canadian High School, Online"
        title="The OSSD Program"
        subtitle="The Ontario Secondary School Diploma — a student-centred pathway to universities worldwide."
        image="https://images.unsplash.com/photo-1523240795612-9a054b0db644"
      />

      {/* What is OSSD */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading center={false} eyebrow="What is OSSD?" title="A world-class Canadian curriculum" />
            <p className="mt-5 text-slate-600 leading-relaxed text-[15px]" data-testid="ossd-intro">{OSSD_INTRO}</p>
            <p className="mt-4 text-slate-600 leading-relaxed text-[15px]">{OSSD_CREDITS}</p>
            <a href="#ossd-apply"><Button className="mt-8 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-12 px-7 font-semibold">Apply for OSSD <ArrowRight size={17} className="ml-1" /></Button></a>          </div>
          <div className="grid grid-cols-2 gap-4">
            {HIGHLIGHTS.map((h, i) => (
              <Reveal key={h.title} delay={i * 60}>
                <div className="h-full bg-[#f7faf8] rounded-2xl p-6 border border-transparent hover:border-[#c9a227]/40 hover-lift">
                  <div className="w-12 h-12 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a]"><h.icon size={22} /></div>
                  <h3 className="font-semibold text-[#012a1e] mt-4">{h.title}</h3>
                  <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">{h.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose */}
      <section className="py-16 bg-[#004030] relative overflow-hidden">
        <div className="absolute -top-20 right-0 w-96 h-96 bg-[#c9a227]/10 rounded-full blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Eyebrow light>Why Choose GDIH Elite</Eyebrow>
          <p className="mt-5 text-white/85 text-lg leading-relaxed" data-testid="ossd-why">{OSSD_WHY}</p>
        </div>
      </section>

      {/* Course content by grade */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Course Content" title="Subjects across Grades 9 to 12" subtitle="Students choose from a broad set of subjects, specialising further in Grades 11 and 12 to align with their intended university pathway." />
          <div className="grid sm:grid-cols-2 gap-6 mt-14">
            {OSSD_GRADES.map((g, i) => (
              <Reveal key={g.grade} delay={i * 60}>
                <div className="h-full bg-[#f7faf8] rounded-2xl p-7 border border-slate-100" data-testid={`ossd-grade-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-11 h-11 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a]"><GraduationCap size={22} /></span>
                    <h3 className="font-display text-xl font-bold text-[#012a1e]">{g.grade}</h3>
                  </div>
                  <ul className="mt-5 grid gap-2">
                    {g.subjects.map((s) => (
                      <li key={s} className="flex items-start gap-2.5 text-slate-600 text-sm">
                        <CheckCircle2 size={16} className="text-[#c9a227] shrink-0 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Admission process */}
      <section className="section-pad bg-[#f4f8f6]">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Admission Process" title="Three simple phases" subtitle="A clear, guided route from application to graduation." />
          <div className="grid md:grid-cols-3 gap-6 mt-14">
            {OSSD_PROCESS.map((p, i) => (
              <Reveal key={p.phase} delay={i * 80}>
                <div className="h-full bg-white rounded-2xl p-7 border border-slate-100 hover-lift">
                  <span className="w-11 h-11 rounded-full bg-[#c9a227] text-[#013d2b] font-display font-bold text-lg grid place-items-center">{i + 1}</span>
                  <h3 className="font-display text-lg font-bold text-[#012a1e] mt-5">{p.phase}</h3>
                  <ul className="mt-4 space-y-2.5">
                    {p.steps.map((s) => (
                      <li key={s} className="flex items-start gap-2.5 text-slate-600 text-sm">
                        <CheckCircle2 size={16} className="text-[#004030] shrink-0 mt-0.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-14 text-center">
            <a href="#ossd-apply"><Button className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-12 px-8 font-semibold">Start your OSSD application <ArrowRight size={17} className="ml-1" /></Button></a>
          </div>
        </div>
      </section>

      {/* Fees & Eligibility */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Before You Apply" title="Fees & eligibility" subtitle="Everything you need to know before registering for the OSSD programme." />
          <div className="grid lg:grid-cols-2 gap-6 mt-14">
            <Reveal>
              <div className="h-full bg-[#f7faf8] rounded-2xl p-8 border border-slate-100" data-testid="ossd-eligibility">
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a]"><ClipboardCheck size={24} /></span>
                  <h3 className="font-display text-2xl font-bold text-[#012a1e]">Eligibility</h3>
                </div>
                <ul className="mt-6 space-y-3.5">
                  {[
                    "Open to students currently in Grades 9 to 12",
                    "An English-medium schooling background",
                    "Recent academic transcripts or report cards",
                    "A laptop/computer and stable internet — the programme is 100% online",
                    "No IELTS or TOEFL required for admission to the programme",
                    "Prior learning is assessed and eligible credits may be transferred",
                  ].map((e) => (
                    <li key={e} className="flex items-start gap-3 text-slate-600">
                      <CheckCircle2 size={19} className="text-[#c9a227] shrink-0 mt-0.5" /> {e}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="h-full bg-[#004030] rounded-2xl p-8 text-white relative overflow-hidden" data-testid="ossd-fees">
                <div className="absolute -top-16 -right-16 w-64 h-64 bg-[#c9a227]/10 rounded-full blur-3xl" />
                <div className="relative">
                  <div className="flex items-center gap-3">
                    <span className="w-12 h-12 rounded-xl bg-[#c9a227] grid place-items-center text-[#013d2b]"><Wallet size={24} /></span>
                    <h3 className="font-display text-2xl font-bold">Fee Structure</h3>
                  </div>
                  <ul className="mt-6 divide-y divide-white/10">
                    {[
                      { label: "One-time registration & application fee", note: "Paid on admission" },
                      { label: "Tuition fee", note: "By grade level & credits" },
                      { label: "Assessment & examination fee", note: "Per academic session" },
                      { label: "Course materials & platform access", note: "Included in tuition" },
                    ].map((f) => (
                      <li key={f.label} className="flex items-center justify-between gap-4 py-3.5">
                        <span className="text-white/90 text-[15px]">{f.label}</span>
                        <span className="text-[#e0c05a] text-sm font-semibold text-right shrink-0">{f.note}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-white/65 text-sm mt-5 leading-relaxed">
                    Exact fees depend on the grade you join and the credits transferred from your prior schooling. Register below to receive your personalised, itemised fee breakdown.
                  </p>
                  <a href="#ossd-apply">
                    <Button className="mt-6 bg-[#c9a227] hover:bg-[#e0c05a] text-[#013d2b] rounded-full h-12 px-7 font-bold">
                      Get my fee breakdown <ArrowRight size={17} className="ml-1" />
                    </Button>
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Enquiry / application form */}
      <section id="ossd-apply" className="section-pad bg-white scroll-mt-24">
        <div className="max-w-3xl mx-auto px-6">
          <SectionHeading eyebrow="Apply Now" title="Register your interest in OSSD" subtitle="Fill in your details and our OSSD team will guide you through eligibility, fees and enrolment." />
          <div className="mt-12"><OSSDForm /></div>
        </div>
      </section>
    </>
  );
}
